import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, HelpCircle, Plus, XCircle } from "lucide-react";
import { useActivateTenant, useSuspendTenant, useTenants } from "@/api/hooks";
import { type DnsRecordStatus, type TenantPlan, type TenantStatus } from "@/api/portalApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

// ── Badge components ──────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: TenantStatus }) {
    const styles: Record<TenantStatus, string> = {
        Active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
        Pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
        Suspended: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
        Cancelled: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
    };
    return (
        <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", styles[status])}>
            {status}
        </span>
    );
}

function PlanBadge({ plan }: { plan: TenantPlan }) {
    const styles: Record<TenantPlan, string> = {
        Starter: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
        Business: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
        Enterprise: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
    };
    return (
        <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", styles[plan])}>
            {plan}
        </span>
    );
}

function DnsBadge({ status }: { status: DnsRecordStatus }) {
    if (status === "Verified") {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Verified
            </span>
        );
    }
    if (status === "Failed") {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-red-700 dark:text-red-400">
                <XCircle className="h-3.5 w-3.5" />
                Issues
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <HelpCircle className="h-3.5 w-3.5" />
            Unknown
        </span>
    );
}

// ── Filter tabs ───────────────────────────────────────────────────────────────

type TabFilter = "All" | TenantStatus;
const tabFilters: TabFilter[] = ["All", "Active", "Pending", "Suspended", "Cancelled"];

// ── Loading skeleton ──────────────────────────────────────────────────────────

function Skeleton({ className }: { className?: string }) {
    return <div className={cn("animate-pulse rounded bg-muted", className)} />;
}

function TableSkeleton() {
    return (
        <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────

function formatDate(value: string) {
    return new Date(value).toLocaleDateString(undefined, { dateStyle: "medium" });
}

export function TenantsPage() {
    const [activeTab, setActiveTab] = useState<TabFilter>("All");
    const [search, setSearch] = useState("");

    const { data: allTenants, isLoading, error } = useTenants();
    const activateMutation = useActivateTenant();
    const suspendMutation = useSuspendTenant();

    const filtered = (allTenants ?? []).filter((t) => {
        const matchesTab = activeTab === "All" || t.status === activeTab;
        const matchesSearch =
            !search ||
            t.companyName.toLowerCase().includes(search.toLowerCase()) ||
            t.domain.toLowerCase().includes(search.toLowerCase());
        return matchesTab && matchesSearch;
    });

    const countByStatus = (status: TenantStatus) =>
        (allTenants ?? []).filter((t) => t.status === status).length;

    function handleActivate(id: string) {
        activateMutation.mutate(id, {
            onError: (err) => alert(`Failed to activate: ${err instanceof Error ? err.message : "Unknown error"}`)
        });
    }

    function handleSuspend(id: string) {
        if (!confirm("Suspend this tenant? They will lose access to all services.")) return;
        suspendMutation.mutate(id, {
            onError: (err) => alert(`Failed to suspend: ${err instanceof Error ? err.message : "Unknown error"}`)
        });
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between gap-4">
                <div>
                    <h1 className="text-xl font-semibold">Tenants</h1>
                    <p className="text-sm text-muted-foreground">
                        {allTenants ? `${allTenants.length} organisation${allTenants.length !== 1 ? "s" : ""}` : ""}
                    </p>
                </div>
                <Button asChild>
                    <Link to="/enroll">
                        <Plus className="h-4 w-4" />
                        New Enrollment
                    </Link>
                </Button>
            </div>

            {/* Error */}
            {error && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    Failed to load tenants: {error instanceof Error ? error.message : "Unknown error"}
                </div>
            )}

            {/* Filter tabs */}
            <div className="flex flex-wrap gap-1 border-b pb-2">
                {tabFilters.map((tab) => {
                    const count = tab === "All" ? (allTenants ?? []).length : countByStatus(tab as TenantStatus);
                    return (
                        <button
                            key={tab}
                            type="button"
                            onClick={() => setActiveTab(tab)}
                            className={cn(
                                "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                                activeTab === tab
                                    ? "bg-primary/10 text-primary"
                                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                            )}
                        >
                            {tab}
                            <span className={cn(
                                "rounded-full px-1.5 py-0.5 text-xs",
                                activeTab === tab ? "bg-primary/20" : "bg-muted"
                            )}>
                                {count}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Search */}
            <Input
                type="search"
                placeholder="Search by company name or domain…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-sm"
            />

            {/* Table */}
            <div className="rounded-lg border bg-card shadow-sm">
                {isLoading ? (
                    <TableSkeleton />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b bg-muted/40 text-left text-xs font-medium text-muted-foreground">
                                    <th className="px-4 py-2.5">Company</th>
                                    <th className="px-4 py-2.5">Domain</th>
                                    <th className="px-4 py-2.5">Plan</th>
                                    <th className="px-4 py-2.5">Users</th>
                                    <th className="px-4 py-2.5">Status</th>
                                    <th className="px-4 py-2.5">DNS</th>
                                    <th className="px-4 py-2.5">Created</th>
                                    <th className="px-4 py-2.5">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                                            {search || activeTab !== "All"
                                                ? "No tenants match your filter"
                                                : "No tenants yet"}
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((t) => (
                                        <tr key={t.id} className="border-b last:border-0 hover:bg-muted/20">
                                            <td className="px-4 py-2.5 font-medium">{t.companyName}</td>
                                            <td className="px-4 py-2.5 font-mono text-xs">{t.domain}</td>
                                            <td className="px-4 py-2.5"><PlanBadge plan={t.plan} /></td>
                                            <td className="px-4 py-2.5 tabular-nums">
                                                {t.maxUsers ?? "∞"}
                                            </td>
                                            <td className="px-4 py-2.5"><StatusBadge status={t.status} /></td>
                                            <td className="px-4 py-2.5"><DnsBadge status={t.dnsStatus} /></td>
                                            <td className="px-4 py-2.5 text-muted-foreground">{formatDate(t.createdAt)}</td>
                                            <td className="px-4 py-2.5">
                                                <div className="flex items-center gap-1">
                                                    <Button variant="ghost" size="sm" asChild>
                                                        <Link to={`/tenants/${t.id}`}>View</Link>
                                                    </Button>
                                                    {t.status === "Pending" || t.status === "Suspended" ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-green-700 hover:text-green-800 dark:text-green-400"
                                                            onClick={() => handleActivate(t.id)}
                                                            disabled={activateMutation.isPending}
                                                        >
                                                            Activate
                                                        </Button>
                                                    ) : null}
                                                    {t.status === "Active" ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-destructive hover:text-destructive"
                                                            onClick={() => handleSuspend(t.id)}
                                                            disabled={suspendMutation.isPending}
                                                        >
                                                            Suspend
                                                        </Button>
                                                    ) : null}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
